import { cmd, UploadCmdType } from "@interlucent/organization-protocol";
import type { UploadCreateReq, UploadCreateAck, UploadSignAck, UploadCompleteReq } from "@interlucent/organization-protocol";
import { waitForAck } from "./utils/waitForAck";
import { getWs } from "./wsConnection";
import type { AppVersionMetadata } from "./types/appVersionMetadata";
import { correlationId } from "@interlucent/ids";
import { logger } from './log';
import { useStore } from "./store";

const log = logger('upload');

type Part = { number: number; blob: Blob; etag?: string };
type UploadMetadata = Partial<AppVersionMetadata>;

export type UploadStartedInfo = {
  uploadId: string;
  key: string;
  totalBytes: number;
  projectId?: string;
  appId?: string;
  replaceVersionId?: string;
};

/* ── Adaptive part size ──────────────────────────────────────────────── */

const MB = 1024 * 1024;

function adaptivePartSize(fileSize: number): number {
  if (fileSize > 10 * 1024 * MB) return 128 * MB;
  if (fileSize > 2 * 1024 * MB) return 64 * MB;
  if (fileSize > 256 * MB) return 32 * MB;
  return 8 * MB;
}

/* ── Retry helpers ───────────────────────────────────────────────────── */

const MAX_RETRIES = 10;
const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;
const BACKOFF_FACTOR = 2;

function retryDelay(attempt: number): number {
  const base = Math.min(BASE_DELAY_MS * BACKOFF_FACTOR ** attempt, MAX_DELAY_MS);
  const jitter = base * 0.25 * (Math.random() * 2 - 1); // ±25% jitter
  return Math.max(0, base + jitter);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryable(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message;

  // waitForAck timeouts and abort signals are retryable
  if (err instanceof DOMException) {
    return err.name === 'TimeoutError' || err.name === 'AbortError';
  }

  // XHR network / timeout errors
  if (msg.startsWith('XHR error')) return true;

  // HTTP status codes
  const statusMatch = msg.match(/PUT failed (\d+)/);
  if (statusMatch) {
    const status = Number(statusMatch[1]);
    return status === 408 || status === 429 || status >= 500;
  }

  // Server-side rejections and paused uploads are NOT retryable
  if (msg === 'upload_paused' || msg === 'No WebSocket connection') return false;

  // Server-side transient errors (S3/R2 failures surfaced via waitForAck)
  if (/please try again|temporarily unavailable/i.test(msg)) return true;

  return false;
}

export async function multipartUploadViaWS(
  file: File,
  onProgress?: (doneBytes:number, total:number)=>void,
  projectId?: string | null,
  onUploadStarted?: (info: UploadStartedInfo) => void,
  resumeUploadId?: string,
  metadata?: UploadMetadata,
  appName?: string,
  appId?: string,
  replaceVersionId?: string,
  checkPaused?: () => boolean
) {
  const ws = getWs();
  if (!ws) throw new Error('No WebSocket connection');

  const baseName = (file.name || 'app').replace(/\.[^.]+$/, '');
  let uploadId: string;
  let key: string;
  let bucket: string;
  let partSize: number;
  let completedParts: { partNumber: number; etag: string }[] = [];

  // RESUME EXISTING UPLOAD
  if (resumeUploadId) {
    log.info(`resuming existing upload: ${resumeUploadId}`);

    // Send upload.resume command to update state: paused -> uploading
    const resumeCorr = correlationId();
    ws.send(JSON.stringify(cmd(UploadCmdType.UploadResume, { uploadId: resumeUploadId }, resumeCorr)));

    // Get upload context from backend (includes completedParts)
    const resumeAck = await waitForAck<any>(resumeCorr);

    uploadId = resumeUploadId;
    key = resumeAck.key;
    bucket = resumeAck.bucket || resumeAck.bucketName;
    partSize = resumeAck.partSize;
    completedParts = resumeAck.completedParts || [];

    log.info('resume context', { uploadId, key, completedPartsCount: completedParts.length });

    onUploadStarted?.({ uploadId, key, totalBytes: file.size, projectId: projectId || undefined, appId: appId || undefined, replaceVersionId: replaceVersionId || undefined });

  } else {
    // CREATE NEW UPLOAD (with retry for transient R2/S3 errors)
    const effectiveMetadata: UploadMetadata | undefined = metadata ? { ...metadata } : undefined;
    const effectiveProjectId = projectId || undefined;
    const effectiveAppId = appId;
    const effectiveReplaceId = replaceVersionId;
    const effectiveAppName = appName || baseName;

    const initReq: UploadCreateReq = {
      filename: file.name,
      size: file.size,
      contentType: file.type || "application/octet-stream",
      partSize: adaptivePartSize(file.size),
      appName: effectiveAppName,
      appSlugHint: effectiveAppName.toLowerCase(),
      ...(effectiveProjectId && { projectId: effectiveProjectId }),
      ...(effectiveAppId && { appId: effectiveAppId }),
      ...(effectiveMetadata?.gameEngine && { gameEngine: effectiveMetadata.gameEngine }),
      ...(effectiveMetadata?.operatingSystem && { operatingSystem: effectiveMetadata.operatingSystem }),
      ...(effectiveMetadata?.executablePath && { executablePath: effectiveMetadata.executablePath }),
      ...(effectiveMetadata?.cmdArgs && { cmdArgs: effectiveMetadata.cmdArgs }),
      ...(effectiveMetadata?.environmentVariables && { environmentVariables: effectiveMetadata.environmentVariables }),
      ...(effectiveMetadata?.streamingProtocolVersion !== undefined && {
        streamingProtocolVersion: effectiveMetadata.streamingProtocolVersion,
      }),
      ...(effectiveMetadata?.workerGameRendezvousSeconds !== undefined && {
        workerGameRendezvousSeconds: effectiveMetadata.workerGameRendezvousSeconds,
      }),
      ...(effectiveReplaceId && { replaceVersionId: effectiveReplaceId })
    };

    let lastCreateError: Error | undefined;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        log.warn('retrying upload creation', { attempt, lastError: lastCreateError?.message });
        await sleep(retryDelay(attempt - 1));
      }
      try {
        const initCorr = correlationId();
        ws.send(JSON.stringify(cmd(UploadCmdType.UploadCreate, initReq, initCorr)));
        const initAck: UploadCreateAck = await waitForAck<UploadCreateAck>(initCorr);
        ({ uploadId, key, bucket, partSize } = initAck);
        break;
      } catch (err) {
        lastCreateError = err instanceof Error ? err : new Error(String(err));
        if (!isRetryable(lastCreateError)) throw lastCreateError;
        log.warn('transient upload creation error', { attempt, error: lastCreateError.message });
        if (attempt === MAX_RETRIES) {
          throw new Error(`Upload creation failed after ${MAX_RETRIES + 1} attempts: ${lastCreateError.message}`);
        }
      }
    }

    onUploadStarted?.({ uploadId: uploadId!, key: key!, totalBytes: file.size, projectId: projectId || undefined, appId: appId || undefined, replaceVersionId: replaceVersionId || undefined });
  }

  const parts: Part[] = [];
  const total = file.size;
  const psize = partSize || 8 * 1024 * 1024;
  let offset = 0, num = 1;
  while (offset < total) {
    const end = Math.min(offset + psize, total);
    const existingPart = completedParts.find(p => p.partNumber === num);
    parts.push({
      number: num++,
      blob: file.slice(offset, end),
      etag: existingPart?.etag // Mark as already uploaded if exists
    });
    offset = end;
  }

  const etags: { partNumber:number; etag:string }[] = [...completedParts];
  let doneBytes = completedParts.reduce((sum, p) => {
    const partIndex = p.partNumber - 1;
    if (partIndex < parts.length) {
      return sum + parts[partIndex].blob.size;
    }
    return sum;
  }, 0);
  const CONCURRENCY = 3;

  // Track in-flight byte progress per part for accurate concurrent progress
  const inflightBytes = new Map<number, number>();

  function totalSentBytes(): number {
    let inflight = 0;
    for (const bytes of inflightBytes.values()) inflight += bytes;
    return Math.min(doneBytes + inflight, total);
  }

  // Update store progress for smooth UI feedback (complements server-side upload.progress events)
  // Throttled to avoid excessive re-renders from frequent XHR progress events
  let lastStoreUpdate = 0;
  const STORE_UPDATE_INTERVAL_MS = 250;
  function reportProgress(force?: boolean) {
    const sent = totalSentBytes();
    onProgress?.(sent, total);
    const now = Date.now();
    if (!force && now - lastStoreUpdate < STORE_UPDATE_INTERVAL_MS) return;
    lastStoreUpdate = now;
    const entry = useStore.getState().uploadProgress[uploadId];
    if (entry) {
      useStore.setState({
        uploadProgress: {
          ...useStore.getState().uploadProgress,
          [uploadId]: { ...entry, sent },
        },
      });
    }
  }

  // Report initial progress if resuming
  if (resumeUploadId && doneBytes > 0) {
    reportProgress(true);
  }

  async function uploadOne(part: Part) {
    // Skip if already completed
    if (part.etag) {
      log.info(`skipping already completed part: ${part.number}`);
      return;
    }

    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        log.warn('retrying part upload', { uploadId, partNumber: part.number, attempt, lastError: lastError?.message });
        await sleep(retryDelay(attempt - 1));
      }

      try {
        // Fresh presigned URL each attempt (eliminates URL-expiry failures)
        const corr = correlationId();
        ws.send(JSON.stringify(cmd(UploadCmdType.UploadSign, { uploadId, key, partNumber: part.number }, corr)));
        const sign: any = await waitForAck<UploadSignAck>(corr);

        // Check if server says part is already completed (PUT succeeded but response lost)
        if (sign.alreadyCompleted && sign.etag) {
          log.info(`part ${part.number} already completed on server`);
          etags.push({ partNumber: part.number, etag: sign.etag });
          inflightBytes.delete(part.number);
          doneBytes += part.blob.size;
          reportProgress(true);
          return;
        }

        inflightBytes.set(part.number, 0);
        const etag = await putWithProgress(sign.url, part.blob, (sent) => {
          inflightBytes.set(part.number, sent);
          reportProgress();
        });
        etags.push({ partNumber: part.number, etag });
        inflightBytes.delete(part.number);
        doneBytes += part.blob.size;
        reportProgress(true);

        // Report to server after part completes (for multi-client sync)
        const repCorr = correlationId();
        ws.send(JSON.stringify(cmd(UploadCmdType.UploadReport, {
          key, uploadId, sentBytes: doneBytes, totalBytes: total, partNumber: part.number, etag
        }, repCorr)));
        return;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (!isRetryable(lastError)) throw lastError;
        log.warn('transient upload error', { partNumber: part.number, attempt, error: lastError.message });
      }
    }

    throw new Error(`part ${part.number} failed after ${MAX_RETRIES + 1} attempts: ${lastError?.message}`);
  }

  // Filter out already completed parts
  const queue = parts.filter(p => !p.etag);
  log.info(`uploading ${queue.length} of ${parts.length} parts`);

  const errors: Error[] = [];
  const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
    while (queue.length) {
      // Check if upload is paused before processing next part
      if (checkPaused?.()) {
        log.info('upload paused, stopping workers');
        break;
      }
      const p = queue.shift()!;
      try {
        await uploadOne(p);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        log.error('part upload failed permanently', { partNumber: p.number, error: error.message });
        errors.push(error);
        break; // this worker stops; others finish in-flight parts
      }
    }
  });
  await Promise.all(workers);

  if (errors.length > 0) {
    const summary = errors.map(e => e.message).join('; ');
    throw new Error(`Upload failed (${errors.length} part error${errors.length > 1 ? 's' : ''}): ${summary}`);
  }

  // If upload was paused, exit early without completing
  if (checkPaused?.()) {
    log.info('upload paused, exiting without completing');
    throw new Error('upload_paused');
  }

  const compCorr = correlationId();
  const compReq: UploadCompleteReq = { uploadId, key, parts: etags.sort((a,b)=>a.partNumber-b.partNumber) };
  ws.send(JSON.stringify(cmd(UploadCmdType.UploadComplete, compReq, compCorr)));
  await waitForAck(compCorr);

  return { key, uploadId, bucket, etags: compReq.parts };
}

// Use XHR to get true upload progress (fetch doesn't expose upload progress reliably)
async function putWithProgress(url: string, blob: Blob, onPartProgress: (sentBytes:number)=>void): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    // At least 5 minutes, or ~10s per MB for slow connections
    xhr.timeout = Math.max(5 * 60_000, (blob.size / (1024 * 1024)) * 10_000);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onPartProgress(e.loaded);
    };
    xhr.ontimeout = () => reject(new Error("XHR error: request timed out"));
    xhr.onerror = () => reject(new Error("XHR error: network failure"));
    xhr.onload = () => {
      const etag = xhr.getResponseHeader("ETag")?.replaceAll('"', "") || "";
      if (xhr.status >= 200 && xhr.status < 300) resolve(etag);
      else reject(new Error(`PUT failed ${xhr.status}`));
    };
    xhr.send(blob);
  });
}
